/**
 * Bandeau haut du jeu (D§3), composition de la maquette `/da`.
 *
 * **Complet dès le premier jour, données ou pas.** Un HUD qui grandit au fil des
 * livraisons donne l'impression d'un chantier ; un HUD complet donne l'impression d'un
 * jeu qui a de la profondeur. La ligue s'affiche donc « Bois · non classé », le compteur
 * de visiteurs « 0 », et le jalon montre le premier objectif réel — aucune de ces zones
 * n'attend une feature pour exister. C'est le même argument que pour les quatre entrées
 * du rail.
 *
 * Ce qui n'existe PAS sans donnée, en revanche, ce sont les pastilles : pas de boost
 * actif, pas de pastille. Une pastille « ×1 » ne dit rien et vole de la place à celles
 * qui diront quelque chose.
 */

import { BUILDINGS } from '@/config/buildings';
import { nextCost } from '@/core/economy';
import { formatClock } from '@/core/format';
import { activeBoosts, buildingCounts, currentProduction } from '@/core/gameState';
import type { CitySnapshot } from '@/core/gameState';
import { nextMilestoneOfKind } from '@/core/milestones';
import type { MilestoneProgress } from '@/core/milestones';
import { n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { createCounter } from './components';
import type { CounterHandle } from './components';
import { icon } from './icons';
import type { IconName } from './icons';

/**
 * Divisions de ligue (§15). Présentes dès maintenant pour que la zone existe ; le
 * classement réel arrive avec les ligues hebdomadaires de la Phase 2.
 */
interface League {
  readonly id: string;
  readonly token: string;
  readonly threshold: number;
  /** Degré au-delà de la dernière division nommée. Absent pour les sept premières. */
  readonly degree?: number;
}

/**
 * Divisions de ligue (§15) — **échelle GÉNÉRÉE, sans dernière marche**.
 *
 * L'échelle était énumérée et s'arrêtait à « Légende ». Un joueur qui l'atteignait
 * voyait « division maximale », c'est-à-dire plus aucun objectif — précisément l'état
 * que le principe premier du §1 interdit, et le même défaut que les paliers de jalons
 * qui s'arrêtaient à un milliard.
 *
 * Les sept premières divisions gardent leur nom et leur couleur ; au-delà, Légende se
 * décline en degrés numérotés, chacun dix fois plus exigeant que le précédent. Trente
 * degrés portent le seuil à 1e63 — hors d'atteinte même après des dizaines de rebirths,
 * et le formateur de `core/format.ts` va bien plus loin encore.
 */
const NAMED_LEAGUES: readonly League[] = [
  { id: 'wood', token: '--league-wood', threshold: 0 },
  { id: 'stone', token: '--league-stone', threshold: 5e3 },
  { id: 'bronze', token: '--league-bronze', threshold: 5e5 },
  { id: 'silver', token: '--league-silver', threshold: 5e7 },
  { id: 'gold', token: '--league-gold', threshold: 5e9 },
  { id: 'diamond', token: '--league-diamond', threshold: 5e11 },
  { id: 'legend', token: '--league-legend', threshold: 5e13 },
];

export const LEAGUES: readonly League[] = [
  ...NAMED_LEAGUES,
  // Degrés de Légende, générés : jamais de dernière marche.
  ...Array.from({ length: 30 }, (_, index) => ({
    id: 'legend',
    token: '--league-legend',
    threshold: 5e13 * 10 ** (index + 1),
    degree: index + 2,
  })),
];

export function leagueFor(totalProduced: number): League {
  let current: League = LEAGUES[0] ?? { id: 'wood', token: '--league-wood', threshold: 0 };
  for (const league of LEAGUES) {
    if (totalProduced >= league.threshold) current = league;
  }
  return current;
}

/** Degré en chiffres romains, jusqu'à 39 — au-delà, on retombe sur le nombre. */
export function romanDegree(degree: number): string {
  const table: readonly [number, string][] = [
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];

  let rest = degree;
  let out = '';
  for (const [value, symbol] of table) {
    while (rest >= value) {
      out += symbol;
      rest -= value;
    }
  }
  return out === '' ? String(degree) : out;
}

export interface HudHandle {
  readonly el: HTMLElement;
  /** Élément du compteur de pièces — cible du vol des pièces (E§1.2). */
  readonly coinsEl: HTMLElement;
  /**
   * Reflète l'état du jeu.
   *
   *  est FOURNI, pas déduit : le HUD ne connaît ni l'horloge ni la durée d'une
   * accélération, et une seconde source de vérité sur « le boost est-il actif » finirait
   * par contredire celle du tick.
   */
  update(
    snapshot: CitySnapshot,
    boost?: number,
    visit?: { readonly visitMultiplier: number; readonly visitUntil: number },
  ): void;
  /** Signale un achat impossible : le compteur pulse en rouge (E§1.3). */
  refuse(): void;
  /**
   * Nombre de joueurs connectés, ou `null` si on ne sait pas.
   *
   * `null` MASQUE le bloc plutôt que d'afficher zéro : annoncer « 0 en ligne » alors
   * qu'on n'a pas réussi à se connecter serait un mensonge.
   */
  setOnline(count: number | null): void;
  /**
   * Célèbre l'arrivée de gemmes.
   *
   * Un nombre qui change sans que rien ne bouge passe inaperçu : le joueur encaisse une
   * quête, la valeur monte de 1 à 5, et il n'est pas certain d'avoir reçu quoi que ce
   * soit. La pulsation est ce qui transforme un changement de texte en récompense.
   */
  celebrateGems(): void;
  destroy(): void;
}

function line(className: string, ...children: (Node | string)[]): HTMLElement {
  const el = document.createElement('div');
  el.className = className;
  el.append(...children);
  return el;
}

export function createHud(onSettings: () => void, onStore?: () => void): HudHandle {
  const el = document.createElement('div');
  el.className = 'hud-top';

  /* ── Marque ──────────────────────────────────────────────────────────────
     Le nom du jeu n'apparaissait nulle part à l'écran. Un wordmark discret, dans la
     police du jeu, en attendant un logo dessiné : ça ne coûte rien et ça fait la
     différence entre « une démo technique » et « un produit ». */
  const brand = document.createElement('div');
  brand.className = 'hud-brand';
  brand.setAttribute('role', 'img');
  brand.setAttribute('aria-label', t('app.name'));

  // Logotype : une tuile isométrique dorée — la forme même du jeu — puis le nom en
  // deux poids. Un texte brut disait le nom sans rien signifier ; une marque doit se
  // reconnaître à sa silhouette avant d'être lue.
  const mark = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  mark.setAttribute('viewBox', '0 0 34 34');
  mark.setAttribute('class', 'hud-brand__mark');
  mark.setAttribute('aria-hidden', 'true');
  mark.innerHTML = [
    '<defs><linearGradient id="v-brand" x1="0" y1="0" x2="0" y2="1">',
    '<stop offset="0" stop-color="#FFD479"/><stop offset="1" stop-color="#E08A1E"/>',
    '</linearGradient></defs>',
    // Bloc de terre, puis face supérieure : la même géométrie que les tuiles du jeu.
    '<path d="M17 12 31 20v5L17 33 3 25v-5z" fill="#8C5518"/>',
    '<path d="M17 5 31 13 17 21 3 13z" fill="url(#v-brand)"/>',
    '<path d="M17 5 31 13 17 21 3 13z" fill="none" stroke="#7A4A16" stroke-width="1.4" stroke-linejoin="round"/>',
    // Petit toit posé dessus : la ville, en une forme.
    '<path d="M17 8.5l5 3-5 3-5-3z" fill="#FFF3D6" opacity=".85"/>',
  ].join('');

  const word = document.createElement('span');
  word.className = 'hud-brand__word';
  word.textContent = t('app.name');

  /* Pastille BETA.
     Elle dit au joueur — et surtout au visiteur qui arrive par un lien partagé — que la
     ville qu'il voit est un chantier. C'est ce qui autorise un défaut à être un défaut
     de jeunesse plutôt qu'un manque de soin, et ça rend la demande de retour légitime.
     Accolée au wordmark : elle qualifie le produit, pas l'écran. */
  const beta = document.createElement('span');
  beta.className = 'hud-brand__beta';
  beta.textContent = t('app.beta');

  brand.append(mark, word, beta);
  brand.setAttribute('aria-label', `${t('app.name')} — ${t('app.beta')}`);

  /* ── Ressources ─────────────────────────────────────────────────────────── */
  const coins: CounterHandle = createCounter({
    value: 0,
    domain: 'coins',
    icon: 'coin',
    size: 'lg',
  });

  /*
    Le débit en forme COMPACTE — « 0/s », et non « 0 par seconde ».

    Le libellé long imposait au bandeau une largeur qui dépendait de la fortune du
    joueur : « 71,7 Md par seconde » mesure plus du double de « 71,7 Md/s ». Un bandeau
    fin ne peut pas se le permettre, et c'est déjà la notation qu'emploient les cartes de
    la boutique (`+0,5/s`) — le HUD s'aligne sur elles au lieu d'inventer sa variante.

    Un seul nœud, donc : la clé `units.perSecond` porte la valeur ET l'unité, ce qui évite
    d'écrire « /s » en dur (§10) et laisse chaque langue placer son unité où elle veut.
  */
  const rateValue = document.createElement('span');
  rateValue.className = 'hud-rate__value';

  const rate = line('hud-rate', rateValue);

  const gems: CounterHandle = createCounter({
    value: 0,
    domain: 'gems',
    icon: 'gem',
    size: 'md',
    /*
      Zéro décimale : les gemmes sont une monnaie ENTIÈRE, gagnée à l'unité par les
      jalons et les quêtes. « 5,0 » se lit comme un défaut d'affichage et brouille
      précisément ce qu'on veut rendre lisible — combien on vient d'en gagner. Les
      pièces, elles, gardent leur décimale : à 10 Hz, une largeur de texte stable évite
      le scintillement (voir `core/format.ts`).
    */
    format: { smallDecimals: 0 },
  });

  // Le compteur de gemmes ouvre la boutique : c'est là que le joueur regarde quand il
  // en manque, pas dans un menu séparé.
  if (onStore !== undefined) {
    gems.el.classList.add('is-actionable');
    gems.el.setAttribute('role', 'button');
    gems.el.setAttribute('tabindex', '0');
    gems.el.setAttribute('aria-label', t('store.title'));
    gems.el.addEventListener('click', onStore);

    /*
      Un « + » explicite à côté du compteur.

      Un compteur cliquable ne se signale pas : rien ne distingue un nombre affiché d'un
      bouton, et le joueur n'essaie pas de cliquer ce qu'il prend pour un affichage. Le
      « + » est la convention universelle du genre — c'est lui qui dit « on peut en
      obtenir plus », sans un mot de texte à traduire.
    */
    const plus = document.createElement('span');
    plus.className = 'hud-gem-plus';
    plus.append(icon('plus'));
    plus.setAttribute('aria-hidden', 'true');
    gems.el.append(plus);
  }

  const pills = document.createElement('div');
  pills.className = 'hud-pills';

  /*
    Le bloc de ressources est le POINT FOCAL du bandeau, et il le dit par son élévation.

    Les trois blocs partageaient `--rest` : le solde du joueur pesait visuellement autant
    que le compteur de visiteurs. `--raised` l'avance d'un cran maintenant que les
    élévations portent une matière distincte et non plus une simple ombre.
  */
  const resources = document.createElement('section');
  resources.className = 'hud-res';
  resources.append(coins.el, rate, gems.el);

  /* ── Statut : ligue + visiteurs + réglages ──────────────────────────────── */
  const leagueIcon = icon('shield', { size: 'large' });

  const leagueName = document.createElement('b');
  const leagueRank = document.createElement('span');

  const leagueText = line('hud-league__text', leagueName, leagueRank);
  const league = line('hud-league', leagueIcon, leagueText);

  const divider = document.createElement('i');
  divider.className = 'hud-divider';

  const visitorCount = document.createElement('span');
  visitorCount.className = 'hud-visitors__num';

  const visitors = line('hud-visitors', icon('eye'), visitorCount);
  visitors.title = t('hud.visitorsToday');

  /*
    Joueurs connectés, à côté des visiteurs.

    Deux nombres voisins mais de nature différente : les visiteurs sont ceux qui sont
    passés sur TA ville, les joueurs en ligne sont ceux qui jouent en ce moment. Le
    point vert pulsant les distingue sans texte — c'est la convention du « direct ».

    Le bloc reste MASQUÉ tant qu'on ne sait pas : afficher « 0 en ligne » alors qu'on
    n'a pas réussi à se connecter serait un mensonge, et « — » un bruit permanent.
  */
  const onlineDot = document.createElement('i');
  onlineDot.className = 'hud-online__dot';

  const onlineCount = document.createElement('span');
  onlineCount.className = 'hud-online__num';

  /*
    Le nombre et le mot vivent dans deux nœuds : sur écran étroit, seul le MOT
    s'efface — le point vert et le nombre restent, et c'est le point qui porte la
    convention du « direct ». L'information ne disparaît jamais, elle se resserre.
  */
  const onlineLabel = document.createElement('span');
  onlineLabel.className = 'hud-online__label';

  const online = line('hud-online', onlineDot, onlineCount, onlineLabel);
  online.hidden = true;

  const onlineDivider = document.createElement('i');
  onlineDivider.className = 'hud-divider';
  onlineDivider.hidden = true;

  const statusBlock = line('hud-status__block', league, divider, visitors, onlineDivider, online);

  const settings = document.createElement('button');
  settings.type = 'button';
  settings.className = 'v-icon-btn hud-settings';
  settings.setAttribute('aria-label', t('settings.title'));
  settings.append(icon('gear'));
  settings.addEventListener('click', onSettings);

  const status = line('hud-status', statusBlock, settings);

  /* ── Progression vers le prochain jalon ─────────────────────────────────── */
  const goalLabel = document.createElement('b');
  const goalValue = document.createElement('span');
  goalValue.className = 'hud-goal__value';

  const barFill = document.createElement('i');
  const bar = line('hud-bar', barFill);

  /*
    Le jalon n'est plus une CARTE posée à côté, il est une ligne DU bloc de ressources.

    En panneau autonome, il mesurait 191 × 94 et s'étendait de x = 194 à x = 385 sur un
    écran de 390 : il occupait donc tout le centre-droit, ce que D§3 interdit sans
    ambiguïté — « Centre : rien. Jamais. » Il coûtait en plus une rangée entière au
    bandeau.

    Le jalon QUALIFIE la production, exactement comme le débit par seconde : sa place est
    sous les compteurs, dans le même panneau, pas à côté d'eux avec le même poids visuel.
  */
  /*
    Le jalon tient sur UNE ligne : libellé, barre, valeur.

    Il occupait deux lignes — libellé et valeur au-dessus, barre en dessous — dans un
    panneau autonome de 94 px de haut. Sur une ligne, la barre prenant l'espace restant
    entre le libellé et la valeur, il coûte 14 px et reste parfaitement lisible. C'est le
    vocabulaire des barres d'expérience de jeu, pas celui d'un tableau de bord.
  */
  const goal = document.createElement('section');
  goal.className = 'hud-goal';
  goal.append(goalLabel, bar, goalValue);

  /*
    UNE SEULE BARRE D'EN-TÊTE POUR TOUT LE JEU.

    Historique des hauteurs sur 390 × 780, tout mesuré : 337 px quand les quatre blocs
    s'empilaient en pleine largeur, puis 260, 198, 175, et 79 px avec deux pastilles
    séparées. Chaque passe gagnait une rangée sans interroger l'architecture.

    Deux pastilles flottantes posaient un problème que le resserrage ne pouvait pas
    résoudre : à elles deux elles occupaient de toute façon la largeur de l'écran, si bien
    que le « centre » restait vide au pixel près et plein à l'œil. Une barre unique dit la
    même chose en une seule forme — c'est le vocabulaire de SimCity BuildIt, et c'est aussi
    ce qui rend l'en-tête IDENTIQUE sur téléphone, tablette et desktop : un seul objet à
    régler, un seul à vérifier.

    Deux rangées internes seulement : les informations sur la première, la progression en
    filet sur la seconde, sur toute la largeur — le vocabulaire d'une barre d'expérience,
    pas celui d'un tableau de bord.
  */
  /*
    Les pastilles de multiplicateur vivent DANS la rangée, après les ressources —
    c'est leur place sémantique : un boost qualifie la production affichée à côté.

    Deux défauts vécus le même jour, et la leçon des deux :
    · dans le BLOC de ressources (un panneau centré verticalement), elles débordaient
      SUR le blason et les compteurs à 390 px — un panneau ne sait pas grandir ;
    · sur une rangée À PART, elles pendaient seules sous l'engrenage en desktop,
      l'en-tête faisait deux lignes avec une première à moitié vide (capture).

    En enfant direct d'une rangée `flex-wrap`, les deux cas se règlent seuls : la
    place manque → un VRAI retour à la ligne qui réserve sa hauteur, jamais un
    chevauchement ; la place suffit → une seule ligne.
  */
  const row = line('hud-top__row', brand, resources, pills, status);
  el.append(row, goal);

  /*
    Le bandeau PUBLIE sa hauteur, il ne la laisse pas deviner.

    Le calque de toasts était calé à `top: 172px` en dur. Or ce bandeau ne fait pas la
    même hauteur partout : sur téléphone il empile marque, ressources et statut ; sur
    desktop tout tient sur une ligne ; en paysage il se resserre encore. À 172 px, le
    toast recouvrait le compteur de pièces sur les mises en page courtes — c'est-à-dire
    qu'il masquait exactement ce qu'il venait de faire bouger — et flottait dans le vide
    sur les autres. C'est le défaut consigné en règle 18 (« le toast qui recouvre le
    compteur »), corrigé à sa cause plutôt qu'en retouchant la constante.

    `ResizeObserver` et non une mesure au démarrage : la hauteur change à la rotation de
    l'appareil, à l'apparition de la barre d'URL mobile, et quand une pastille de boost
    entre dans le bloc de ressources.
  */
  /*
    On publie le BAS du bandeau dans le viewport, pas sa hauteur propre.

    C'est ce dont l'appelant a besoin — « à partir d'où l'écran est libre » — et c'est la
    seule valeur qui reste juste quand le bandeau est décalé par l'encoche (`--safe-top`).
    Une hauteur seule obligerait chaque consommateur à réadditionner la marge de sécurité,
    donc à la redécouvrir, donc à se tromper.
  */
  const publishBottom = (): void => {
    document.documentElement.style.setProperty(
      '--hud-height',
      `${String(Math.round(el.getBoundingClientRect().bottom))}px`,
    );
  };

  const heightObserver = new ResizeObserver(publishBottom);
  heightObserver.observe(el);

  function pill(className: string, iconName: IconName | null, text: string): HTMLElement {
    const span = document.createElement('span');
    span.className = `hud-pill ${className}`;
    if (iconName !== null) span.append(icon(iconName));
    span.append(text);
    return span;
  }

  return {
    el,
    coinsEl: coins.el,

    update(snapshot, boost = 1, visit) {
      coins.setValue(snapshot.coins);
      gems.setValue(snapshot.gems);
      // Le débit affiché est celui qui CRÉDITE réellement : boost compris, sinon le
      // compteur monterait plus vite que le chiffre censé l'expliquer.
      rateValue.textContent = t('units.perSecond', {
        value: n(currentProduction(snapshot, boost)),
      });

      // Pastilles : seulement ce qui a quelque chose à dire.
      pills.replaceChildren();
      /*
        Chaque multiplicateur porte son COMPTE À REBOURS (demande de Benoit) : « ×2 »
        sans échéance obligeait à deviner combien de temps il restait. Une pastille
        PAR SOURCE — l'accélération gemmes et le boost de visite n'expirent pas
        ensemble, et une pastille unique ne pourrait porter qu'une seule échéance.
        Rafraîchi à 10 Hz par le tick : le rebours vit sans minuterie dédiée.
      */
      const now = Date.now();
      /*
        UNE PASTILLE PAR ACCÉLÉRATION ACTIVE, et non plus une seule.

        Les emplacements sont séparés depuis G§3 A3 : une pub gratuite et une
        accélération achetée peuvent courir en même temps, avec des échéances
        différentes. Une pastille unique ne pourrait porter qu'un seul rebours —
        elle mentirait sur l'autre, ou l'effacerait.

        L'icône dit la SOURCE : l'éclair pour ce qui est acheté, l'œil pour ce qui
        vient des visiteurs, l'étoile pour un événement. Le joueur voit d'où vient
        son bonus, donc ce qu'il perd en le laissant expirer.
      */
      for (const boost of activeBoosts(snapshot, now)) {
        pills.append(
          pill(
            'hud-pill--boost',
            boost.source === 'ad' ? 'eye' : boost.source === 'event' ? 'star' : 'bolt',
            `×${n(boost.multiplier)} · ${formatClock(boost.until - now)}`,
          ),
        );
      }
      const visitMult = visit?.visitMultiplier ?? 1;
      if (visitMult > 1) {
        const left = (visit?.visitUntil ?? 0) - now;
        pills.append(
          pill(
            'hud-pill--boost',
            'eye',
            left > 0 ? `×${n(visitMult)} · ${formatClock(left)}` : `×${n(visitMult)}`,
          ),
        );
      }
      // Filet : un boost composite sans détail (aucune des deux pastilles ci-dessus)
      // reste visible — jamais une production ×4 sans explication à l'écran.
      if (boost > 1 && pills.childElementCount === 0) {
        pills.append(pill('hud-pill--boost', 'coin', `×${n(boost)}`));
      }
      if (snapshot.prestigeMultiplier > 1) {
        pills.append(pill('hud-pill--prestige', 'star', `×${n(snapshot.prestigeMultiplier)}`));
      }

      const current = leagueFor(snapshot.totalProduced);
      // « Légende II », « Légende III »… : le nom reste, le degré dit qu'on progresse
      // encore. Un chiffre romain se lit comme un rang, pas comme une quantité.
      const base = t(`leagues.${current.id}` as TranslationKey);
      leagueName.textContent =
        current.degree === undefined ? base : `${base} ${romanDegree(current.degree)}`;
      leagueName.style.color = `var(${current.token})`;
      leagueIcon.style.color = `var(${current.token})`;

      /*
        Progression vers la division suivante, et non « non classé ».

        « Or · non classé » était contradictoire : la division vient de la production
        totale, le rang serait hebdomadaire et n'existe pas encore. Surtout, « non
        classé » n'apprend rien et ne donne rien à viser — ce que le principe premier
        du §1 interdit. Une progression chiffrée donne toujours un objectif.
      */
      const nextLeague = LEAGUES.find((league) => league.threshold > snapshot.totalProduced);
      leagueRank.textContent =
        nextLeague === undefined
          ? t('hud.leagueMax')
          : t('hud.leagueNext', {
              value: n(Math.max(0, nextLeague.threshold - snapshot.totalProduced)),
            });

      /*
        Le blason PORTE la division par sa couleur ; sur téléphone, le texte s'efface.

        Un bandeau fin ne peut pas garder « Bois · plus que 5,0 k » : ce seul libellé
        imposait 216 px au bloc de statut, qui recouvrait alors le centre de l'écran. Le
        blason est déjà teinté à la couleur de la division — c'est lui qui dit le rang, le
        mot le répétait. L'infobulle et le panneau Progression gardent la phrase entière,
        donc rien ne devient inaccessible : l'information change de place, elle ne
        disparaît pas.
      */
      league.title = `${leagueName.textContent} — ${leagueRank.textContent}`;

      visitorCount.textContent = String(snapshot.visitorsReceived);

      // Prochain jalon : un objectif chiffré et proche, jamais un espace réservé.
      // On préfère un jalon de production, et on retombe sur le premier disponible —
      // la barre doit toujours dire quelque chose, même au tout premier chargement.
      const progressState: MilestoneProgress = {
        buildingCounts: buildingCounts(snapshot),
        landPurchases: snapshot.landPurchases,
        roadCells: snapshot.islands[0]?.roads.length ?? 0,
        decorations: snapshot.islands[0]?.traces.length ?? 0,
        visitorsReceived: snapshot.visitorsReceived,
        totalProduced: snapshot.totalProduced,
        prestigeLevel: snapshot.prestigeLevel,
      };
      const claimed = new Set(snapshot.claimedMilestones);

      const next =
        nextMilestoneOfKind('production', progressState, claimed) ??
        nextMilestoneOfKind('first-building', progressState, claimed) ??
        nextMilestoneOfKind('land', progressState, claimed);

      if (next === null) {
        goalLabel.textContent = t('hud.goalBuildings');
        goalValue.textContent = `${String(BUILDINGS.length)} / ${String(BUILDINGS.length)}`;
        barFill.style.width = '100%';
      } else {
        // Le paramètre est tantôt un nombre (paliers), tantôt un type de bâtiment
        // (premier exemplaire) : on ne le formate en nombre que s'il en est un.
        const param = next.milestone.labelParam;
        goalLabel.textContent = t(
          next.milestone.labelKey as TranslationKey,
          param === undefined
            ? {}
            : {
                value:
                  typeof param === 'number' ? n(param) : t(`buildings.${param}` as TranslationKey),
              },
        );
        goalValue.textContent = `${n(next.current)} / ${n(next.milestone.threshold)}`;
        barFill.style.width = `${(100 * next.ratio).toFixed(1)}%`;
      }
    },

    setOnline(count) {
      const known = count !== null && count > 0;
      online.hidden = !known;
      onlineDivider.hidden = !known;
      if (known) {
        onlineCount.textContent = n(count);
        onlineLabel.textContent = t('hud.online');
        online.title = t('hud.onlineTitle');
      }
    },

    celebrateGems() {
      gems.el.classList.remove('is-collected');
      // Forcer un reflow relance l'animation si deux encaissements s'enchaînent.
      void gems.el.offsetWidth;
      gems.el.classList.add('is-collected');
    },

    refuse() {
      coins.el.classList.remove('is-refused');
      void coins.el.offsetWidth;
      coins.el.classList.add('is-refused');
    },

    destroy() {
      heightObserver.disconnect();
      el.remove();
    },
  };
}

/** Prix du prochain exemplaire — exporté pour l'en-tête du panneau latéral. */
export function nextPriceOf(snapshot: CitySnapshot, type: string): number {
  const definition = BUILDINGS.find((b) => b.type === type);
  if (definition === undefined) return 0;
  return nextCost(definition, buildingCounts(snapshot)[type] ?? 0);
}
